import { InjectionToken, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

/**
 * SSR-safe replacement for @ng-toolkit/universal (deprecated since Angular 8).
 *
 * Fournit WINDOW et LOCAL_STORAGE comme InjectionToken qui marchent à la
 * fois côté browser et côté Node (SSR). Les composants/services injectent
 * ces tokens au lieu d'utiliser `window.localStorage` directement, ce qui
 * éviterait les crashes SSR ("localStorage is not defined").
 *
 * Usage :
 *   constructor(@Inject(WINDOW) private window: Window) {}
 *   constructor(@Inject(LOCAL_STORAGE) private storage: Storage) {}
 */

export const WINDOW = new InjectionToken<Window | object>('WINDOW', {
  providedIn: 'root',
  factory: () => {
    const document = inject(DOCUMENT);
    // En SSR, document.defaultView est undefined → on retourne un mock vide
    // pour éviter les crashes lors d'accès comme window.location
    return document.defaultView ?? ({} as Window);
  },
});

export const LOCAL_STORAGE = new InjectionToken<Storage>('LOCAL_STORAGE', {
  providedIn: 'root',
  factory: () => {
    const platformId = inject(PLATFORM_ID);
    if (isPlatformBrowser(platformId)) {
      return window.localStorage;
    }
    // SSR fallback : no-op Storage qui ne lance pas d'erreur quand appelé
    return {
      length: 0,
      clear: () => {},
      getItem: () => null,
      key: () => null,
      removeItem: () => {},
      setItem: () => {},
    } as Storage;
  },
});
